import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import { GET_SCHEDULE_RANGE } from "../../../../utils/GraphQL/queries";

/**
 * "Is After" chain selector — calendar-event-types plan §7.4.
 *
 * Self-fetches the day's other events via `getScheduleRange` scoped to the
 * event's day bounds. The alternative (prop-drilling the parent modal's
 * items list) was rejected for Phase 6 to keep modal changes minimal —
 * the extra fetch runs only when a modal is open and is a single day's
 * worth of data (tiny).
 *
 * Controlled component:
 *   dayStartMs: epoch ms of the event's start (or the modal's pending start).
 *     Used to derive the day boundary for the `getScheduleRange` call.
 *   currentEventId: the _id of the event being created/edited (or null for
 *     a new event with no id yet). Used to filter self out of the options.
 *   chainGroupId: current chainGroupId string (or null).
 *   selectedAfterId: _id of the event selected in the dropdown (parent-owned).
 *   onChange(nextAfterId): fires when the dropdown changes.
 *   onBreakChain(): fires when "Break Chain" is clicked.
 *   eventType: the current event's eventType. Returns null when eventType is
 *     "vacation" — vacation is never chain-eligible (§7.4 / §6.12).
 */
export default function ChainSelector({
  dayStartMs,
  currentEventId,
  chainGroupId,
  selectedAfterId,
  onChange,
  onBreakChain,
  eventType,
}) {
  // Compute local-day bounds for the fetch. Handles DST cleanly because we
  // construct new DateTime objects at 00:00:00 and 23:59:59 of the same
  // calendar day rather than adding Duration intervals.
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (!dayStartMs) return { rangeStart: null, rangeEnd: null };
    const n = parseInt(dayStartMs, 10);
    if (!Number.isFinite(n)) return { rangeStart: null, rangeEnd: null };
    const d = new Date(n);
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    return {
      rangeStart: start.getTime().toString(),
      rangeEnd: end.getTime().toString(),
    };
  }, [dayStartMs]);

  const { data, loading } = useQuery(GET_SCHEDULE_RANGE, {
    variables: { rangeStart, rangeEnd, onlyGetInvoiced: false },
    skip: !rangeStart || !rangeEnd || eventType === "vacation",
    fetchPolicy: "cache-and-network",
  });

  const options = useMemo(() => {
    const raw = data?.getScheduleRange || [];
    return raw
      .filter((e) => {
        const ci = e?.calendarItem;
        if (!ci) return false;
        if (ci.id === currentEventId) return false;
        const et = ci.eventType || "job";
        if (et === "vacation") return false; // §6.12
        return true;
      })
      .sort((a, b) => {
        const aMs = parseInt(a.calendarItem.startMs || "0", 10);
        const bMs = parseInt(b.calendarItem.startMs || "0", 10);
        return aMs - bMs;
      });
  }, [data, currentEventId]);

  // When the event is already in a chain but parent hasn't seeded a selection,
  // surface the implicit anchor: the nearest earlier same-group sibling by
  // startMs. Falls back to the nearest later sibling if the current event is
  // the anchor itself (first in the chain by time). Gives the user visual
  // confirmation of "you're chained to X" instead of an empty dropdown.
  const computedAnchorId = useMemo(() => {
    if (!chainGroupId || !dayStartMs) return "";
    const myStartMs = parseInt(dayStartMs, 10);
    if (!Number.isFinite(myStartMs)) return "";
    const sameGroup = options.filter((e) => {
      const cg = e?.calendarItem?.chainGroupId;
      return cg && String(cg) === String(chainGroupId);
    });
    if (sameGroup.length === 0) return "";
    const earlier = [...sameGroup]
      .filter((e) => parseInt(e.calendarItem.startMs || "0", 10) < myStartMs)
      .sort(
        (a, b) =>
          parseInt(b.calendarItem.startMs || "0", 10) -
          parseInt(a.calendarItem.startMs || "0", 10)
      );
    if (earlier.length > 0) return earlier[0].calendarItem.id;
    const later = [...sameGroup].sort(
      (a, b) =>
        parseInt(a.calendarItem.startMs || "0", 10) -
        parseInt(b.calendarItem.startMs || "0", 10)
    );
    return later[0]?.calendarItem?.id || "";
  }, [chainGroupId, dayStartMs, options]);

  // Once the user explicitly picks in the dropdown (including the "— none —"
  // option), we respect their choice and stop auto-displaying the computed
  // anchor. Otherwise a user deselecting would immediately snap back to the
  // anchor value visually.
  const [hasUserTouched, setHasUserTouched] = useState(false);

  // Early return MUST come AFTER all hooks are called — React hooks rule.
  if (eventType === "vacation") return null;

  // Compact am/pm time — e.g. "8am", "1:30pm". Matches the banner labels
  // admins already read on the calendar blocks.
  const formatShortTime = (msStr) => {
    const ms = parseInt(msStr, 10);
    if (!Number.isFinite(ms)) return "";
    const d = new Date(ms);
    const h = d.getHours();
    const m = d.getMinutes();
    const mer = h >= 12 ? "pm" : "am";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === 0
      ? `${h12}${mer}`
      : `${h12}:${String(m).padStart(2, "0")}${mer}`;
  };

  const labelFor = (e) => {
    const ci = e.calendarItem;
    const et = ci.eventType || "job";
    const first = e.customer?.firstName || "";
    const last = e.customer?.lastName || "";
    const name = `${first} ${last}`.trim();
    const startStr = formatShortTime(ci.startMs);
    const endStr = formatShortTime(ci.endMs);
    // Time suffix: "8am - 2pm" when both bounds exist, else just the start.
    const timeSuffix = startStr
      ? endStr
        ? ` — ${startStr} - ${endStr}`
        : ` — ${startStr}`
      : "";
    const display = name || (et === "go_by" ? "Client" : "Unnamed job");
    if (et === "go_by") return `Go By — ${display}${timeSuffix}`;
    return `${display}${timeSuffix}`;
  };

  const handleSelect = (nextId) => {
    setHasUserTouched(true);
    if (!nextId) {
      onChange({ targetId: null, targetChainGroupId: null });
      return;
    }
    const match = options.find((e) => e.calendarItem.id === nextId);
    onChange({
      targetId: nextId,
      targetChainGroupId: match?.calendarItem?.chainGroupId || null,
    });
  };

  // Display value: respect user pick once they've interacted; otherwise show
  // the computed anchor when the event is chained.
  const displaySelectedId = hasUserTouched
    ? selectedAfterId || ""
    : selectedAfterId || computedAnchorId;

  return (
    <div className="create-event-field">
      <span className="create-event-field-label">Is After (optional)</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <BdrSelectPopup
          id="chain-selector"
          className="bdr-input bdr-select-trigger chain-selector-trigger"
          value={displaySelectedId || ""}
          onChange={(e) => handleSelect(e.target.value || null)}
          disabled={loading && options.length === 0}
          placeholder="— none —"
          options={options.map((e) => ({
            value: e.calendarItem.id,
            label: labelFor(e),
          }))}
        />
        {chainGroupId && (
          <button
            type="button"
            onClick={onBreakChain}
            style={{
              padding: "6px 12px",
              background: "#b71c1c",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Break Chain
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#b0b0cc", marginTop: 4 }}>
        Chained events share one refinisher assignment — assigning any member
        cascades to the whole chain.
      </div>
    </div>
  );
}
